/** Shared domain types for Virtuous (web + API). */
export const TIER_LABELS = {
    1: "Tier 1 — Network root / Architect",
    1.5: "Tier 1.5 — Root secretary / EA",
    2: "Tier 2 — Business owner",
    2.5: "Tier 2.5 — Owner's secretary",
    3: "Tier 3 — Employee / agent / driver / sales",
    3.5: "Tier 3.5 — High-end agent's secretary",
    4: "Tier 4 — Customer / end user",
};
export const TIER_BLURBS = {
    1: "Top of the tree. Meets business owners and builds the network.",
    1.5: "Supports Tier 1; can help administer IDs under Tier 1's authority.",
    2: "Business owner in the network.",
    2.5: "Secretary for a business owner.",
    3: "People using the system day to day under a business.",
    3.5: "Secretary for a high-end Tier 3 person.",
    4: "Customers / end users. They do not issue Unique IDs, but can recommend people to meet their Leadership.",
};
/** Who may administer / issue Unique IDs (someone above the recipient — never Tier 4) */
export const TIERS_THAT_CAN_ISSUE_IDS = [1, 1.5, 2, 2.5, 3, 3.5];
export function canIssueUniqueId(tier) {
    return TIERS_THAT_CAN_ISSUE_IDS.includes(tier);
}
/**
 * Tier 4 (and everyone) can recommend a person to meet their upline.
 * That grows the network by word of mouth without letting Tier 4 issue IDs.
 */
export function canRecommendToUpline(_tier) {
    return true;
}
/**
 * You may only issue an ID for a tier strictly below you
 * (someone above them administers their ID).
 */
export function canIssueTier(issuerTier, recipientTier) {
    if (!canIssueUniqueId(issuerTier))
        return false;
    return recipientTier > issuerTier;
}
export const ALL_TIERS = [1, 1.5, 2, 2.5, 3, 3.5, 4];
/**
 * Who may assign whom in the network tree:
 * - Tier 1 / 1.5 → assign leads to Tier 2 (business owners)
 * - Tier 2 → **route their leads to Tier 3 workers themselves**
 * - Tier 2 → **may also authorize Tier 2.5** to route those same leads to Tier 3
 * - Tier 2.5 (only if authorized by that Tier 2) → route that owner's leads to Tier 3
 * - Tier 3 → primary workers (receive / work leads; do not reassign up the tree)
 */
export function canAssignAsOwner(actorTier) {
    return actorTier === 1 || actorTier === 1.5;
}
/**
 * Who may route a lead to a Tier 3 primary worker:
 * - Tier 2 always (on leads they own)
 * - Tier 2.5 only when their Tier 2 has authorized them as a delegate
 * - Tier 1 / 1.5 may also place workers when routing
 */
export function canAssignAsWorker(actorTier, opts) {
    if (actorTier === 2)
        return true;
    if (actorTier === 2.5 && opts?.isAuthorizedDelegate)
        return true;
    if (actorTier === 1 || actorTier === 1.5)
        return true;
    return false;
}
/** Tier 2 owners may grant routing authority to their Tier 2.5. */
export function canAuthorizeWorkerRoutingDelegate(actorTier) {
    return actorTier === 2;
}
/** Primary workers for day-to-day lead work */
export function isPrimaryLeadWorker(tier) {
    return tier === 3 || tier === 3.5;
}
/**
 * Network leadership: Tier 1 (root) + Tier 1.5 (root secretary / EA).
 * @tag leadership
 */
export function isNetworkLeadership(tier) {
    const t = Number(tier);
    return t === 1 || t === 1.5;
}
/**
 * ## Product rule — Virtuous **source code**
 * **Only Tier 1 and Tier 1.5** may read repository / application source files
 * (browse `apps/`, `packages/`, internal docs that map the codebase).
 *
 * Tier 2 and below never get source listings or file contents via the product API.
 * Partner Lead Sources branding remains **Tier 1 only** (separate, stricter rule).
 * Vault credentials are also leadership-scoped (ops), not the same as source code.
 *
 * @tag source-code
 */
export function canViewSourceCode(tier) {
    return isNetworkLeadership(tier);
}
/** @deprecated Prefer canViewSourceCode — same gate (T1 | 1.5) */
export function canViewSystemCode(tier) {
    return canViewSourceCode(tier);
}
/**
 * Password vault / credential secrets — **Tier 1 only**
 * (not 1.5, 2, 2.5, 3, 3.5, or 4).
 */
export function canReadSensitiveFiles(tier) {
    return Number(tier) === 1;
}
export const COMPANY_DEPARTMENT_LABELS = {
    purchasing: "Purchasing",
    logistics: "Logistics",
    sales: "Sales",
    operations: "Operations",
    finance: "Finance",
    warehouse: "Warehouse / Inventory",
    other: "Other",
};
/**
 * Departments that may **Connect with a Logistics Team**.
 * Purchasing (inbound/outbound freight on buys) and Logistics (ops).
 */
export const DEPARTMENTS_CAN_CONNECT_LOGISTICS = [
    "purchasing",
    "logistics",
];
export function canDepartmentConnectLogistics(department) {
    return DEPARTMENTS_CAN_CONNECT_LOGISTICS.includes(department);
}
export const LOGISTICS_PARTNER_KIND_LABELS = {
    carrier: "Carrier",
    freight_forwarder: "Freight forwarder",
    trucking_broker: "Trucking broker",
};
export const INSURANCE_PARTNER_KIND_LABELS = {
    insurance_company: "Insurance company",
    reinsurance_company: "Reinsurance company",
};
export const INSURANCE_FOCUS_LINE_LABELS = {
    marine_cargo: "Marine cargo",
    hull: "Hull",
    liability: "Liability",
    property: "Property",
    property_cat: "Property catastrophe",
    cyber: "Cyber",
    directors_officers: "D&O",
    workers_comp: "Workers' compensation",
    trade_credit: "Trade credit",
    surety: "Surety",
    auto: "Auto / fleet",
    aviation: "Aviation",
    energy: "Energy",
    other: "Other",
};
export function insuranceBestFocusStrength(focuses) {
    if (!focuses.length)
        return 0;
    return Math.max(...focuses.map((f) => Math.max(0, Math.min(100, Number(f.strength) || 0))));
}
export function insuranceFocusMatchesQuery(focus, query) {
    const q = normalizeLaneToken(query);
    if (!q)
        return true;
    const lineLabel = INSURANCE_FOCUS_LINE_LABELS[focus.line] || focus.line;
    const hay = normalizeLaneToken(`${focus.line} ${lineLabel} ${focus.label} ${focus.region || ""}`);
    return q.split(" ").filter(Boolean).every((p) => hay.includes(p));
}
/**
 * Best (max) lane strength for a team — used when ranking without a lane filter.
 */
export function logisticsBestLaneStrength(lanes) {
    if (!lanes.length)
        return 0;
    return Math.max(...lanes.map((l) => Math.max(0, Math.min(100, Number(l.strength) || 0))));
}
/** @deprecated use logisticsBestLaneStrength on lanes */
export function logisticsOverallStrength(b) {
    const parts = [
        b.reliability,
        b.capacity,
        b.coverage,
        b.value,
        b.networkFit,
    ].map((n) => Math.max(0, Math.min(100, Number(n) || 0)));
    const avg = parts.reduce((a, x) => a + x, 0) / parts.length;
    return Math.round(avg * 10) / 10;
}
/** Normalize for lane search (case-insensitive, strip fillers) */
export function normalizeLaneToken(s) {
    return s
        .trim()
        .toLowerCase()
        .replace(/\s+/g, " ")
        .replace(/\b(to|and|&|–|—|-)\b/g, " ")
        .replace(/\s+/g, " ")
        .trim();
}
/**
 * Whether a lane matches a user search (e.g. "Puerto Rico", "Miami to PR", "Brazil").
 */
export function laneMatchesQuery(lane, query) {
    const q = normalizeLaneToken(query);
    if (!q)
        return true;
    const hay = normalizeLaneToken(`${lane.origin} ${lane.destination} ${lane.label}`);
    const parts = q.split(" ").filter(Boolean);
    return parts.every((p) => hay.includes(p));
}
/**
 * Who may configure company ERP + department links (vault for secrets).
 * Tier 1 / 1.5 (network ops) and Tier 2 (business owners for their company).
 */
export function canConfigureErpIntegrations(tier) {
    const t = Number(tier);
    return t === 1 || t === 1.5 || t === 2;
}
/** Who may place a purchase request that routes into the company ERP */
export function canSubmitErpPurchase(tier) {
    const t = Number(tier);
    // Owners, secretaries, workers — not pure Tier 4 customers by default
    return t === 1 || t === 1.5 || t === 2 || t === 2.5 || t === 3 || t === 3.5;
}
export const LEAD_GEO_SCOPE_LABELS = {
    local: "Local (ZIPs / metro)",
    multi_state: "Multi-state (US)",
    usa: "USA nationwide",
    international: "International",
};
/** Parse multi-value geo lists: commas, newlines, or semicolons. */
export function parseGeoList(raw) {
    if (raw == null)
        return [];
    const parts = Array.isArray(raw) ? raw : raw.split(/[\n,;]+/);
    const out = [];
    const seen = new Set();
    for (const p of parts) {
        const v = p.trim();
        if (!v)
            continue;
        const key = v.toLowerCase();
        if (seen.has(key))
            continue;
        seen.add(key);
        out.push(v);
    }
    return out;
}
/** Normalize US state to 2-letter when possible. */
export function normalizeStateCode(state) {
    const s = state.trim().toLowerCase();
    const map = {
        alabama: "al",
        alaska: "ak",
        arizona: "az",
        arkansas: "ar",
        california: "ca",
        colorado: "co",
        connecticut: "ct",
        delaware: "de",
        florida: "fl",
        georgia: "ga",
        hawaii: "hi",
        idaho: "id",
        illinois: "il",
        indiana: "in",
        iowa: "ia",
        kansas: "ks",
        kentucky: "ky",
        louisiana: "la",
        maine: "me",
        maryland: "md",
        massachusetts: "ma",
        michigan: "mi",
        minnesota: "mn",
        mississippi: "ms",
        missouri: "mo",
        montana: "mt",
        nebraska: "ne",
        nevada: "nv",
        "new hampshire": "nh",
        "new jersey": "nj",
        "new mexico": "nm",
        "new york": "ny",
        "north carolina": "nc",
        "north dakota": "nd",
        ohio: "oh",
        oklahoma: "ok",
        oregon: "or",
        pennsylvania: "pa",
        "rhode island": "ri",
        "south carolina": "sc",
        "south dakota": "sd",
        tennessee: "tn",
        texas: "tx",
        utah: "ut",
        vermont: "vt",
        virginia: "va",
        washington: "wa",
        "west virginia": "wv",
        wisconsin: "wi",
        wyoming: "wy",
        "district of columbia": "dc",
        "washington dc": "dc",
        "washington d.c.": "dc",
    };
    if (s.length === 2)
        return s;
    return map[s] || s;
}
export function leadFlowEditorPermissions(tier, opts) {
    const t = Number(tier);
    if (t === 1 || t === 1.5) {
        return {
            canViewStudio: true,
            canEditLayout: true,
            canSaveTemplates: true,
            canActivateNetworkTemplate: true,
            canEditMatchCriteria: true,
            canEditCapacity: true,
            canEditSlaDefaults: true,
            canToggleAutoEdges: true,
            canAuthorizeDelegate: false,
            canRouteToTier3: true,
            canAssignOwner: true,
            allowedGeoScopes: ["local", "multi_state", "usa", "international"],
            templateScope: "network",
        };
    }
    if (t === 2) {
        return {
            canViewStudio: true,
            canEditLayout: true,
            canSaveTemplates: true,
            canActivateNetworkTemplate: false,
            canEditMatchCriteria: true,
            canEditCapacity: true,
            canEditSlaDefaults: false,
            canToggleAutoEdges: false,
            canAuthorizeDelegate: true,
            canRouteToTier3: true,
            canAssignOwner: false,
            // Multi ZIP + multi state markets
            allowedGeoScopes: ["local", "multi_state"],
            templateScope: "team",
        };
    }
    if (t === 2.5) {
        const auth = Boolean(opts?.isAuthorizedDelegate);
        return {
            canViewStudio: true,
            canEditLayout: false,
            canSaveTemplates: false,
            canActivateNetworkTemplate: false,
            canEditMatchCriteria: false,
            canEditCapacity: false,
            canEditSlaDefaults: false,
            canToggleAutoEdges: false,
            canAuthorizeDelegate: false,
            canRouteToTier3: auth,
            canAssignOwner: false,
            allowedGeoScopes: [],
            templateScope: "none",
        };
    }
    if (t === 3 || t === 3.5) {
        return {
            canViewStudio: true,
            canEditLayout: false,
            canSaveTemplates: false,
            canActivateNetworkTemplate: false,
            // Local service area so workers hit local goals
            canEditMatchCriteria: true,
            canEditCapacity: true,
            canEditSlaDefaults: false,
            canToggleAutoEdges: false,
            canAuthorizeDelegate: false,
            canRouteToTier3: false,
            canAssignOwner: false,
            allowedGeoScopes: ["local"],
            templateScope: "none",
        };
    }
    return {
        canViewStudio: true,
        canEditLayout: false,
        canSaveTemplates: false,
        canActivateNetworkTemplate: false,
        canEditMatchCriteria: false,
        canEditCapacity: false,
        canEditSlaDefaults: false,
        canToggleAutoEdges: false,
        canAuthorizeDelegate: false,
        canRouteToTier3: false,
        canAssignOwner: false,
        allowedGeoScopes: [],
        templateScope: "none",
    };
}
/** Edges that may be toggled or emphasized — never invent out-of-tier paths. */
export const LEAD_FLOW_ALLOWED_EDGES = [
    { id: "e1", from: "webhook", to: "hold", minTierToToggle: null },
    { id: "e2", from: "hold", to: "dedupe", minTierToToggle: null },
    { id: "e3", from: "dedupe", to: "match", minTierToToggle: null },
    { id: "e4", from: "match", to: "t1", minTierToToggle: 1 },
    { id: "e5", from: "match", to: "t2", minTierToToggle: 1 },
    { id: "e6", from: "t1", to: "t2", minTierToToggle: 1 },
    { id: "e7", from: "t2", to: "t3", minTierToToggle: 2 },
    { id: "e8", from: "t2", to: "t25", minTierToToggle: 2 },
    { id: "e9", from: "t25", to: "t3", minTierToToggle: 2 },
    { id: "e10", from: "t3", to: "sla", minTierToToggle: null },
    { id: "e11", from: "sla", to: "audit", minTierToToggle: null },
    { id: "e12", from: "match", to: "audit", minTierToToggle: null },
];
/**
 * Locked referral fee framework (receiver-facing total).
 * Platform fee fixed 8%. Referring agent cut V = 10–22%. Total T = V + 8 (max 30%).
 *
 * | Band     | V (agent) | Platform | T (receiver) |
 * | Low      | 10%       | 8%       | 18%          |
 * | Standard | 12%       | 8%       | 20%          |
 * | High     | 22%       | 8%       | 30%          |
 */
export const REFERRAL_PLATFORM_FEE_PCT = 8;
export const REFERRAL_AGENT_CUT_MIN = 10;
export const REFERRAL_AGENT_CUT_MAX = 22;
export const REFERRAL_AGENT_CUT_DEFAULT = 12;
/** Total at low / default / high for display checks */
export const REFERRAL_TOTAL_MIN = REFERRAL_AGENT_CUT_MIN + REFERRAL_PLATFORM_FEE_PCT; // 18
export const REFERRAL_TOTAL_DEFAULT = REFERRAL_AGENT_CUT_DEFAULT + REFERRAL_PLATFORM_FEE_PCT; // 20
export const REFERRAL_TOTAL_MAX = REFERRAL_AGENT_CUT_MAX + REFERRAL_PLATFORM_FEE_PCT; // 30
/** Clamp V and return locked { V, platform, T } — only trusted fee math */
export function lockReferralFees(agentCutPct) {
    const v = Math.min(REFERRAL_AGENT_CUT_MAX, Math.max(REFERRAL_AGENT_CUT_MIN, Math.round(Number(agentCutPct) || REFERRAL_AGENT_CUT_DEFAULT)));
    const platformFeePct = REFERRAL_PLATFORM_FEE_PCT;
    const totalFeePct = v + platformFeePct;
    return { agentCutPct: v, platformFeePct, totalFeePct };
}
export function referralTotalFeePct(agentCutPct) {
    return lockReferralFees(agentCutPct).totalFeePct;
}
/** True only if client-sent fees match the locked formula */
export function isValidReferralFeeMath(input) {
    const locked = lockReferralFees(input.agentCutPct);
    if (input.agentCutPct !== locked.agentCutPct)
        return false;
    if (input.platformFeePct != null &&
        input.platformFeePct !== locked.platformFeePct) {
        return false;
    }
    if (input.totalFeePct != null && input.totalFeePct !== locked.totalFeePct) {
        return false;
    }
    if (locked.totalFeePct < REFERRAL_TOTAL_MIN || locked.totalFeePct > REFERRAL_TOTAL_MAX) {
        return false;
    }
    return true;
}
export function referralFeeBand(agentCutPct) {
    const v = lockReferralFees(agentCutPct).agentCutPct;
    if (v <= REFERRAL_AGENT_CUT_MIN)
        return "low";
    if (v >= REFERRAL_AGENT_CUT_MAX)
        return "high";
    return "standard";
}
/**
 * Myreferral brokerage (licensed channel) + Virtuous (link platform).
 * Dashboard & agent profile used when brokerage details are submitted.
 */
export const MYREFERRAL = {
    brand: "Myreferral brokerage",
    site: "https://myreferralbrokerage.com",
    agentDashboard: "https://myreferralbrokerage.com/agent/dashboard",
    /** Architect / root agent profile on Myreferral */
    agentProfile: "https://myreferralbrokerage.com/a/michael-dunlap",
    agentSlug: "michael-dunlap",
};
/** Parties in the automated referral agreement */
export const REFERRAL_AGREEMENT_PARTIES = {
    platform: "Virtuous Trade & Consulting",
    brokerage: MYREFERRAL.brand,
    brokerageLink: MYREFERRAL.brand,
    agentDashboardUrl: MYREFERRAL.agentDashboard,
    agentProfileUrl: MYREFERRAL.agentProfile,
    role: "Virtuous is the referral link platform; Myreferral brokerage is the participating brokerage channel for licensed real estate referrals.",
};
export function buildReferralAgreementSummary(input) {
    const { agentCutPct: v, platformFeePct, totalFeePct: t } = lockReferralFees(input.agentCutPct);
    return [
        `REFERRAL FEE AGREEMENT (locked at submit)`,
        `Math check: T = V + ${platformFeePct}%  →  ${t}% = ${v}% + ${platformFeePct}%  (range total ${REFERRAL_TOTAL_MIN}–${REFERRAL_TOTAL_MAX}%).`,
        `Referring agent cut (V): ${v}% of receiving broker’s side.`,
        `Platform fee (Virtuous): ${platformFeePct}% fixed.`,
        `Total fee to receiving broker (T): ${t}%.`,
        `Lead type: ${input.categoryLabel}. Contact: ${input.contactName}.`,
        `${REFERRAL_AGREEMENT_PARTIES.role}`,
        `Brokerage channel: ${MYREFERRAL.brand}`,
        `Agent dashboard: ${MYREFERRAL.agentDashboard}`,
        `Agent profile (link): ${MYREFERRAL.agentProfile}`,
        `Network platform: ${REFERRAL_AGREEMENT_PARTIES.platform}`,
        `By submitting, the referring party accepts these locked percentages and that this deal ties into Myreferral brokerage (dashboard + agent profile above) with Virtuous as the referral link platform.`,
    ].join("\n");
}
export const MEETING_METHODS = [
    { value: "zoom", label: "Zoom" },
    { value: "google_meet", label: "Google Meet" },
    { value: "microsoft_teams", label: "Microsoft Teams" },
    { value: "discord", label: "Discord" },
    { value: "phone", label: "Phone call" },
    { value: "facetime", label: "FaceTime" },
    { value: "in_person", label: "In person" },
    { value: "other", label: "Other" },
];
export const REFERRAL_CATEGORIES = [
    { value: "residential_buy", label: "Residential — Buy" },
    { value: "residential_sell", label: "Residential — Sell" },
    { value: "residential_rent", label: "Residential — Rent" },
    { value: "commercial_buy", label: "Commercial — Buy" },
    { value: "commercial_sell", label: "Commercial — Sell" },
    { value: "commercial_lease", label: "Commercial — Lease" },
    { value: "multifamily", label: "Multifamily" },
    { value: "industrial", label: "Industrial / Warehouse" },
    { value: "retail", label: "Retail" },
    { value: "hospitality", label: "Hospitality" },
    { value: "office", label: "Office" },
    { value: "land", label: "Land" },
    { value: "business_service", label: "Business Service" },
    { value: "trade", label: "Licensed Trade" },
    { value: "finance", label: "Finance / Loans" },
    { value: "other", label: "Other" },
];
/** Real estate specializations (agents may send or receive by type) */
export const REAL_ESTATE_REFERRAL_TYPES = [
    {
        value: "residential_buy",
        label: "Residential — Buy",
        blurb: "Buyer clients",
    },
    {
        value: "residential_sell",
        label: "Residential — Sell",
        blurb: "Seller clients",
    },
    {
        value: "residential_rent",
        label: "Residential — Rent",
        blurb: "Lease / rental",
    },
    {
        value: "commercial_buy",
        label: "Commercial — Buy",
        blurb: "Commercial purchase",
    },
    {
        value: "commercial_sell",
        label: "Commercial — Sell",
        blurb: "Commercial listing / sale",
    },
    {
        value: "commercial_lease",
        label: "Commercial — Lease",
        blurb: "Commercial tenant / landlord lease",
    },
    {
        value: "multifamily",
        label: "Multifamily",
        blurb: "Apartments / multi-unit",
    },
    {
        value: "industrial",
        label: "Industrial",
        blurb: "Warehouse / industrial only",
    },
    {
        value: "retail",
        label: "Retail",
        blurb: "Retail real estate",
    },
    {
        value: "hospitality",
        label: "Hospitality",
        blurb: "Hotels / hospitality",
    },
    {
        value: "office",
        label: "Office",
        blurb: "Office space",
    },
    {
        value: "land",
        label: "Land",
        blurb: "Vacant land / development",
    },
];
/** Types shown for send/receive based on member industry home */
export function referralTypesForIndustry(industry) {
    if (industry === "real_estate")
        return REAL_ESTATE_REFERRAL_TYPES;
    if (industry === "trades")
        return [
            {
                value: "trade",
                label: "Licensed trade",
                blurb: "Contractors and trades",
            },
            { value: "other", label: "Other service", blurb: "Related handoff" },
        ];
    if (industry === "finance")
        return [
            {
                value: "finance",
                label: "Finance / loans",
                blurb: "Mortgage, loans, payments",
            },
            { value: "other", label: "Other", blurb: "Related handoff" },
        ];
    // General / others: full list condensed from categories
    return REFERRAL_CATEGORIES.map((c) => ({
        value: c.value,
        label: c.label,
        blurb: "",
    }));
}
export const INDUSTRY_TEMPLATES = [
    {
        value: "real_estate",
        label: "Real estate",
        blurb: "Buy, sell, rent, and property referrals.",
    },
    {
        value: "trades",
        label: "Trades & services",
        blurb: "Licensed trades and home/business services.",
    },
    {
        value: "finance",
        label: "Finance",
        blurb: "Loans, payments, and money solutions.",
    },
    {
        value: "shipping",
        label: "Shipping & logistics",
        blurb: "Freight, shipping, and supply chain.",
    },
    {
        value: "retail",
        label: "Retail & products",
        blurb: "Products, MarketTime-style offers.",
    },
    {
        value: "professional_services",
        label: "Professional services",
        blurb: "Consulting, legal, accounting, and more.",
    },
    {
        value: "general",
        label: "General / not sure yet",
        blurb: "A simple home base until you pick a focus.",
    },
];
/** Short labels for 2-column buttons; blurbs for tooltips / detail lines */
export const REAL_ESTATE_SERVICE_AREAS = [
    { id: "lawn_landscape", label: "Lawn", blurb: "Landscaping and yard care" },
    { id: "trash_bin_cleaning", label: "Bin cleaning", blurb: "Trash bin wash" },
    { id: "electrician", label: "Electric", blurb: "Electrical service" },
    { id: "plumber", label: "Plumbing", blurb: "Plumbing service" },
    { id: "hvac", label: "HVAC", blurb: "Heating and cooling" },
    { id: "roofer", label: "Roofing", blurb: "Roof repair or replace" },
    { id: "painter", label: "Painting", blurb: "Interior and exterior" },
    { id: "pest_control", label: "Pest control", blurb: "Pest and termite" },
    { id: "pool", label: "Pool", blurb: "Pool care" },
    { id: "cleaning", label: "Cleaning", blurb: "Home cleaning" },
    { id: "handyman", label: "Handyman", blurb: "Small repairs" },
    { id: "general_contractor", label: "Contractor", blurb: "General contractor" },
    { id: "appliance", label: "Appliances", blurb: "Appliance service" },
    { id: "flooring", label: "Flooring", blurb: "Floor install" },
    { id: "windows_doors", label: "Windows", blurb: "Windows and doors" },
    { id: "security", label: "Security", blurb: "Alarms and cameras" },
    { id: "moving", label: "Moving", blurb: "Moving and storage" },
    { id: "lawyer", label: "Legal", blurb: "Real estate attorney" },
    { id: "title_escrow", label: "Title", blurb: "Title and escrow" },
    { id: "home_inspector", label: "Inspection", blurb: "Home inspection" },
    { id: "appraiser", label: "Appraisal", blurb: "Property valuation" },
    { id: "insurance", label: "Insurance", blurb: "Home insurance" },
    { id: "mortgage", label: "Mortgage", blurb: "Lending" },
    { id: "surveyor", label: "Survey", blurb: "Land survey" },
    { id: "architect", label: "Architect", blurb: "Design" },
    { id: "interior_design", label: "Design", blurb: "Interior design" },
    { id: "other", label: "Other", blurb: "Not listed" },
];
export function serviceAreaLabel(idOrLabel) {
    const hit = REAL_ESTATE_SERVICE_AREAS.find((a) => a.id === idOrLabel || a.label === idOrLabel);
    return hit?.label ?? idOrLabel;
}
export const GRADE_SECTION_KEYS = [
    "price",
    "responseTime",
    "professionalism",
    "punctuality",
];
/** Labels for UI — each section is equal weight (25%). */
export const GRADE_SECTIONS = [
    { key: "price", label: "Price" },
    { key: "responseTime", label: "Response Time" },
    { key: "professionalism", label: "Professionalism" },
    { key: "punctuality", label: "Punctuality" },
];
/** Equal weight per section */
export const GRADE_SECTION_WEIGHT = 0.25;
const GRADE_SCALE = [
    { letter: "A+", score: 4.3 },
    { letter: "A", score: 4.0 },
    { letter: "A-", score: 3.7 },
    { letter: "B+", score: 3.3 },
    { letter: "B", score: 3.0 },
    { letter: "B-", score: 2.7 },
    { letter: "C+", score: 2.3 },
    { letter: "C", score: 2.0 },
    { letter: "C-", score: 1.7 },
    { letter: "D", score: 1.0 },
    { letter: "F", score: 0 },
];
export function gradeLetterToScore(letter) {
    const t = letter.trim().toUpperCase();
    const hit = GRADE_SCALE.find((g) => g.letter === t);
    return hit?.score ?? 3.0;
}
export function scoreToGradeLetter(score) {
    if (Number.isNaN(score))
        return "B";
    // Nearest letter on the scale
    let best = GRADE_SCALE[0];
    let bestDist = Math.abs(score - best.score);
    for (const g of GRADE_SCALE) {
        const d = Math.abs(score - g.score);
        if (d < bestDist) {
            best = g;
            bestDist = d;
        }
    }
    return best.letter;
}
/** Overall letter grade = equal average of the four sections. */
export function overallGradeFromBreakdown(breakdown) {
    const scores = GRADE_SECTION_KEYS.map((k) => gradeLetterToScore(String(breakdown[k] ?? "B")));
    const avg = scores.reduce((a, b) => a + b, 0) / scores.length;
    return scoreToGradeLetter(avg);
}
export function isProviderGradeBreakdown(v) {
    if (!v || typeof v !== "object")
        return false;
    const o = v;
    return GRADE_SECTION_KEYS.every((k) => typeof o[k] === "string" && String(o[k]).trim().length > 0);
}
